// === FEATURE: Static FM — synthesized Web Audio engine ===
//
// A self-contained, asset-free radio synth. There are NO audio files: every
// sound is generated with oscillators + a noise buffer. The engine models a
// real FM receiver:
//
//   - STATION audio: a warm ambient drone (2-3 detuned oscillators through a
//     lowpass filter, with a slow LFO tremolo) whose pitch is derived from the
//     station index over a pentatonic scale.
//   - STATIC audio: looping white noise through a bandpass filter whose gain
//     scales with how FAR the needle is from the nearest station — loud between
//     stations, near-silent when locked. Tuning across the band therefore sounds
//     like sweeping a real dial: static fades, a station emerges.
//
// All audio passes through one master GainNode -> destination. Master gain is 0
// (silent) unless POWER is ON *and* the `sound` prop is true; changes are ramped
// with setTargetAtTime so toggling power/sound never clicks.
//
// Browsers block audio until a user gesture, so the AudioContext is created
// lazily on first ensure() (called from a click handler) and resume()d then.
//
// Lifecycle: construct -> ensure() (on first gesture) -> setPower / setSound /
// setTuning(...) as state changes -> dispose() on unmount (stops every node,
// disconnects, closes the context). No always-on timers; oscillators/noise are
// suspended whenever the master is effectively silent so an off / off-screen
// radio costs nothing.

// Drone roots — DERIVED FROM THE DESIGN PALETTE (see "THE AMBULATE SCALE"
// below). The cabinet is anchored on ambu-red (#b0190c, the wordmark): we re-root
// the whole platform tone-set on A (A2 = 55Hz/110Hz) — a warmer, more resonant
// root than the old D2, matching the red-warm brushed-metal cabinet. Each station
// takes a degree of the A natural-minor-hexatonic palette, ordered by its
// stations.json swatch hue so the drone pitch tracks the visual colour:
//   0 WRMM #fefa12 yellow  -> A  (root, bright)
//   1 KILN #12fe8d green   -> E  (the fifth, neon-green lift)
//   2 QVEX #f476ff magenta -> C  (minor third, airy)
//   3 WOMB #ff7a59 orange  -> D  (warm fourth, low)
//   4 KRRT #00a4bd teal    -> G  (the seventh, cool)
// Low/warm so the drones sit UNDER the visuals. (A2, E2, C3, D3, G2.)
const STATION_ROOTS = [110.0, 82.41, 130.81, 146.83, 98.0];

// Smoothing time constants (seconds) for setTargetAtTime ramps.
const MASTER_TAU = 0.08; // power / sound mute fade
const STATIC_TAU = 0.06; // static follows the dial quickly but smoothly
const DRONE_TAU = 0.35; // stations crossfade in/out gently
const VOICE_TAU = 0.25; // the glossolalia voice crossfades in/out gently
const MELODY_TAU = 0.3; // the melody bus crossfades in/out gently

// Target levels (pre-master). Kept conservative so the piece is ambient.
const STATIC_MAX = 0.16; // loudest static (needle between stations)
const DRONE_LEVEL = 0.12; // a fully tuned station drone
const VOICE_LEVEL = 0.1; // the unknown-tongue transmission, peak (under drone)
const MELODY_LEVEL = 0.09; // the station's melodic motif, peak (a touch under drone)

// === MELODIC MOTIF (per station) ==========================================
// Every tuned station, beneath its drone + voice, plays a short looping
// musical phrase — a melodic "signature" unique to that station so sweeping
// the band reveals a different tune at each lock. There are NO samples: each
// note is a fresh short-lived oscillator + a percussive ADSR gain (a pluck /
// bell) connected to a melody bus -> master. A self-rescheduling setTimeout
// chain (NOT a per-frame loop) steps through the motif, looping it with slight
// algorithmic variation (octave jumps / neighbour swaps / rests) so it evolves.
//
// THE AMBULATE SCALE — DERIVED FROM THE DESIGN PALETTE, shared by every tone on
// the platform for cohesion. The cabinet's anchor colour is ambu-red (#b0190c,
// the wordmark), a warm dark hue: we root the scale on A (Root A2 = MIDI 45).
// The palette is A NATURAL MINOR PENTATONIC (A, C, D, E, G) plus the b5 (Eb) as a
// lo-fi "blue note" / CRT-detune colour tone — the flat-fifth gives the metallic,
// slightly-out-of-tune shimmer the design's brushed-metal + CRT character calls
// for, while the pentatonic core keeps every combination consonant in any order
// so the runtime variation never lands sour. Notes are MIDI numbers converted to
// Hz at play time. (Replaces the old D-minor-pentatonic + flat-2 set.)
const MIDI_A2 = 45; // root of the Ambulate scale (ambu-red anchor)

// MIDI -> frequency (equal temperament, A4 = MIDI 69 = 440Hz).
function midiToFreq(midi) {
  return 440 * Math.pow(2, (midi - 69) / 12);
}

// Scale degrees of A natural minor pentatonic as semitone offsets from A, with
// the b5 (Eb, +6) available as a lo-fi blue/CRT colour tone. Octave is added per
// note. MELODIES below reference absolute MIDI numbers (built from MIDI_A2 +
// degree + 12*octave) so each station gets its own register & contour.
//   A=+0  C=+3  D=+5  Eb=+6(blue colour)  E=+7  G=+10
const A = MIDI_A2; // 45 (A2) — root
const C = MIDI_A2 + 3; // minor third
const D = MIDI_A2 + 5; // fourth
const Eb = MIDI_A2 + 6; // b5 — lo-fi blue / CRT-detune colour tone
const E = MIDI_A2 + 7; // fifth
const G = MIDI_A2 + 10; // minor seventh

// One motif per station index. Each is a short on-scale phrase in a distinct
// register & contour, plus a step interval (ms) and a pluck timbre, so every
// station is clearly its own tune. Notes are absolute MIDI numbers; `null` is a
// rest. Kept low/mid (octaves +0..+2 above D2) with the odd high pluck.
// Each motif is drawn from the A-minor-hexatonic palette above; the per-station
// REGISTER, CONTOUR, TEMPO and TIMBRE are derived from that station's
// stations.json swatch (hue -> brightness/register, saturation -> grit). Timbres
// lean lo-fi/metallic/CRT: square/sawtooth plucks with brighter, grittier
// cutoffs than the old warm-sine set, so the radio reads as the brushed-metal,
// scanline cabinet rather than a soft ambient pad.
const MELODIES = [
  {
    // Station 0 WRMM #fefa12 (bright yellow) — high, ringing, lo-fi square bell.
    // Rooted on A up high, with the Eb blue note glinting through.
    notes: [A + 12, E + 12, C + 12, Eb + 12, E + 12, A + 24, null, G + 12],
    step: 360,
    type: "square",
    cutoff: 2600, // bright, glassy-metallic (matches the hot yellow swatch)
    decay: 0.42,
  },
  {
    // Station 1 KILN #12fe8d (neon green) — mid-high, quick rising runs on the
    // fifth (E). Triangle with a hard edge for a chiptune-green lift.
    notes: [E, G, A + 12, C + 12, E + 12, C + 12, A + 12, null],
    step: 280,
    type: "triangle",
    cutoff: 1900,
    decay: 0.36,
  },
  {
    // Station 2 QVEX #f476ff (magenta) — sparse, airy, high. Lots of space; a
    // square pluck so the few notes still read metallic/CRT, not soft.
    notes: [C + 12, null, E + 12, null, A + 12, null, G + 12, null],
    step: 540,
    type: "square",
    cutoff: 2200,
    decay: 0.66,
  },
  {
    // Station 3 WOMB #ff7a59 (warm orange) — busy, mid-low, syncopated weave
    // around the root & fourth. Sawtooth for a warm, gritty brushed-metal buzz.
    notes: [A, D, A, G, C, D, C, A],
    step: 230,
    type: "sawtooth",
    cutoff: 1300,
    decay: 0.3,
  },
  {
    // Station 4 KRRT #00a4bd (cool teal) — wide leaps, lo-fi blue colour (Eb),
    // call-and-answer. Square plucks, low-mid, cool and detuned-feeling.
    notes: [A, Eb + 12, C, null, D, E, null, A + 12],
    step: 400,
    type: "square",
    cutoff: 1450,
    decay: 0.48,
  },
];

// All Ambulate-scale degrees as absolute MIDI offsets (no octave) for runtime
// neighbour-swap variation — swapping to any of these keeps the note on-scale.
// A natural-minor-pentatonic + the b5 (Eb) blue/CRT colour tone.
const SCALE_PCS = [A, C, D, Eb, E, G].map((m) => m - MIDI_A2); // semitone classes

// === GLOSSOLALIA voice ====================================================
// A tuned station does not just drone: a voice murmurs in it. We synthesize a
// "speaking in tongues" transmission — believable but unintelligible syllables
// in an unknown language. There are NO recordings: a glottal sawtooth source
// (vocal fundamental + vibrato) is shaped by three parallel bandpass "formant"
// filters into morphing vowels, gated by a syllable-envelope, then squeezed
// through a telephone-band "transmission" filter so it reads as a far-off radio
// numbers-station. A self-rescheduling setTimeout chain (NOT a per-frame loop)
// emits syllables grouped into word-like bursts with consonant pings between.

// Approximate formant triples (F1,F2,F3 in Hz) for five vowels. These are the
// resonances that make the ear hear "ah / eh / ee / oh / oo".
const VOWEL_FORMANTS = {
  a: [800, 1150, 2900],
  e: [400, 1700, 2600],
  i: [350, 2100, 2700],
  o: [450, 800, 2830],
  u: [325, 700, 2530],
};
const VOWEL_KEYS = ["a", "e", "i", "o", "u"];

// Smoothing for the formant filters as vowels morph, and for the per-syllable
// loudness envelope. Short so speech sounds articulated, not slurred.
const FORMANT_TAU = 0.05; // ~40-90ms vowel glide (set per syllable)
const SYLLABLE_TAU = 0.02; // fast open/close so syllables read as distinct

export default class FMAudio {
  constructor() {
    this.ctx = null;
    this.master = null;

    // Real-output analyser: taps the master mix so the SIGNAL meter reacts to
    // exactly what is playing (drone + glossolalia + per-station melody +
    // static). Created lazily with the rest of the graph; its byte buffer is
    // allocated once and reused every frame (no per-frame GC).
    this.analyser = null;
    this.analyserData = null; // reused Uint8Array of frequency bins

    // Static chain.
    this.noiseSrc = null;
    this.noiseFilter = null;
    this.noiseGain = null;

    // Drone chain (rebuilt per station).
    this.droneGain = null; // crossfade gain for the whole drone voice
    this.droneFilter = null;
    this.droneOscs = []; // the detuned oscillators
    this.lfo = null; // tremolo LFO
    this.lfoGain = null;
    this.currentStationIdx = null;

    // Glossolalia voice chain (rebuilt per station).
    this.voiceGain = null; // master crossfade for the whole voice
    this.voiceTransFilter = null; // telephone/radio transmission bandpass
    this.voiceSyllGain = null; // per-syllable loudness envelope
    this.voiceFormants = []; // 3 parallel bandpass formant filters (F1,F2,F3)
    this.voiceOsc = null; // glottal sawtooth source
    this.voiceVibLfo = null; // vibrato LFO on the source
    this.voiceVibGain = null;
    this.voiceConsFilter = null; // consonant-burst noise filter
    this.voiceConsGain = null; // consonant-burst envelope
    this.voiceConsNoise = null; // looping noise feeding the consonant burst
    this.currentVoiceIdx = null;
    this.voiceParams = null; // per-station derived voice character
    this.voiceTimer = null; // setTimeout handle for the syllable scheduler
    this.voiceRunning = false; // is the scheduler currently scheduling?
    this.voiceVowel = "a"; // last vowel emitted (for occasional repeats)

    // Melodic motif chain (the bus is fixed; per-note oscs are ephemeral).
    this.melodyBus = null; // crossfade gain for the whole melody voice
    this.melodyFilter = null; // shared lowpass shaping the plucks
    this.currentMelodyIdx = null;
    this.melodyParams = null; // per-station motif (notes/step/timbre)
    this.melodyTimer = null; // setTimeout handle for the note sequencer
    this.melodyRunning = false; // is the sequencer currently scheduling?
    this.melodyStep = 0; // index into the current motif

    // Desired logical state (applied to gains whenever the graph exists).
    this.power = false;
    this.sound = true;
    this.tunedIdx = null; // station index currently locked, or null
    this.staticAmount = 1; // 0 (locked) .. 1 (far between stations)

    this.disposed = false;
    this.started = false;
  }

  // --- Lazy creation on first user gesture ---------------------------------
  // Safe to call repeatedly; only builds the graph once. Resumes a suspended
  // context (the gesture requirement) every call so a power-on always sounds.
  ensure() {
    if (this.disposed) return;
    if (typeof window === "undefined") return;
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return;

    if (!this.ctx) {
      this.ctx = new AC();
      this._buildGraph();
      this.started = true;
    }
    if (this.ctx.state === "suspended" && this.ctx.resume) {
      // Best-effort; ignore the promise rejection if the gesture wasn't enough.
      this.ctx.resume().catch(() => {});
    }
    // Push current logical state into the freshly-built (or existing) graph.
    this._applyMaster();
    this._applyStatic();
    this._applyDrone();
    this._applyVoice();
    this._applyMelody();
  }

  _buildGraph() {
    const ctx = this.ctx;

    // Master -> destination. Starts silent.
    this.master = ctx.createGain();
    this.master.gain.value = 0;
    this.master.connect(ctx.destination);

    // Analyser tap: master ALSO feeds an AnalyserNode (a sink — it does not pass
    // audio onward), so the SIGNAL meter can read the real station output. This
    // is an ADDITIONAL connection; the master -> destination path above is left
    // untouched so audio is never interrupted.
    const analyser = ctx.createAnalyser();
    analyser.fftSize = 256; // 128 frequency bins — plenty for a 14-bar meter
    analyser.smoothingTimeConstant = 0.7; // gentle temporal smoothing
    this.master.connect(analyser);
    this.analyser = analyser;
    this.analyserData = new Uint8Array(analyser.frequencyBinCount);

    // --- Static: looping white noise -> bandpass -> gain -> master ---
    const noiseBuf = this._makeNoiseBuffer(ctx, 2.0);
    const src = ctx.createBufferSource();
    src.buffer = noiseBuf;
    src.loop = true;

    const filt = ctx.createBiquadFilter();
    filt.type = "bandpass";
    filt.frequency.value = 4200; // tinny radio hiss
    filt.Q.value = 0.6;

    const ng = ctx.createGain();
    ng.gain.value = 0;

    src.connect(filt);
    filt.connect(ng);
    ng.connect(this.master);
    src.start();

    this.noiseSrc = src;
    this.noiseFilter = filt;
    this.noiseGain = ng;

    // --- Drone: detuned oscillators -> lowpass -> droneGain -> master ---
    const dgain = ctx.createGain();
    dgain.gain.value = 0;
    dgain.connect(this.master);

    const dfilt = ctx.createBiquadFilter();
    dfilt.type = "lowpass";
    // Open a touch + a gentle resonant peak so a little metallic upper-harmonic
    // edge survives (brushed-metal / CRT character) rather than a pure warm pad.
    dfilt.frequency.value = 1250;
    dfilt.Q.value = 1.1;
    dfilt.connect(dgain);

    this.droneGain = dgain;
    this.droneFilter = dfilt;

    // Slow tremolo LFO modulating the drone gain for "life".
    const lfo = ctx.createOscillator();
    lfo.type = "sine";
    lfo.frequency.value = 0.18;
    const lfoGain = ctx.createGain();
    lfoGain.gain.value = 0; // depth set when a station is selected
    lfo.connect(lfoGain);
    lfoGain.connect(dgain.gain);
    lfo.start();
    this.lfo = lfo;
    this.lfoGain = lfoGain;

    // Build oscillators for whatever station is currently tuned (if any).
    this._buildOscsFor(this.tunedIdx);

    // --- Voice: build the persistent (station-independent) transmission tail.
    //   formants -> syllable-env -> transmission bandpass -> voiceGain -> master
    // The per-station source/formants/consonant nodes are (re)built by
    // _buildVoiceFor(idx); here we only stand up the fixed downstream chain.
    const vgain = ctx.createGain();
    vgain.gain.value = 0;
    vgain.connect(this.master);
    this.voiceGain = vgain;

    // Telephone / shortwave band: narrows the voice to a far-off radio. Pushed a
    // touch higher & tighter (narrower band, higher Q) for a grittier, more
    // bandlimited lo-fi/CRT transmission character in keeping with the design.
    const vtrans = ctx.createBiquadFilter();
    vtrans.type = "bandpass";
    vtrans.frequency.value = 1600; // centre of the squeezed comms band
    vtrans.Q.value = 1.1;
    vtrans.connect(vgain);
    this.voiceTransFilter = vtrans;

    // Per-syllable loudness gate (opened/closed by the scheduler). Starts shut.
    const vsyll = ctx.createGain();
    vsyll.gain.value = 0;
    vsyll.connect(vtrans);
    this.voiceSyllGain = vsyll;

    // Build the source + formants + consonant burst for the current station.
    this._buildVoiceFor(this.tunedIdx);

    // --- Melody: a shared bus + lowpass that ephemeral per-note plucks feed.
    //   (each note) -> melodyFilter -> melodyBus -> master
    const mbus = ctx.createGain();
    mbus.gain.value = 0; // crossfaded in by _applyMelody when tuned & audible
    mbus.connect(this.master);
    this.melodyBus = mbus;

    const mfilt = ctx.createBiquadFilter();
    mfilt.type = "lowpass";
    // Default cutoff (per-station value applied in _buildMelodyFor). A bit of
    // resonance gives the plucks a ringing, glassy-metallic edge in keeping with
    // the brushed-metal / CRT design rather than a dull warm pluck.
    mfilt.frequency.value = 1800;
    mfilt.Q.value = 1.4;
    mfilt.connect(mbus);
    this.melodyFilter = mfilt;

    // Pick the motif for whatever station is currently tuned (if any).
    this._buildMelodyFor(this.tunedIdx);
  }

  // White-noise buffer, `seconds` long, filled once (no per-frame randomness).
  _makeNoiseBuffer(ctx, seconds) {
    const len = Math.floor(ctx.sampleRate * seconds);
    const buf = ctx.createBuffer(1, len, ctx.sampleRate);
    const ch = buf.getChannelData(0);
    for (let i = 0; i < len; i++) ch[i] = Math.random() * 2 - 1;
    return buf;
  }

  // (Re)create the detuned oscillator voices for a given station index. Tears
  // down the previous set first. Pass null to leave the drone voiceless.
  _buildOscsFor(idx) {
    if (!this.ctx || !this.droneFilter) return;
    // Stop & disconnect old oscillators.
    for (const o of this.droneOscs) {
      try {
        o.stop();
      } catch (e) {}
      try {
        o.disconnect();
      } catch (e) {}
    }
    this.droneOscs = [];
    this.currentStationIdx = idx;
    if (idx == null) return;

    const ctx = this.ctx;
    const root = STATION_ROOTS[idx % STATION_ROOTS.length] || 130.81;

    // 4 voices: root, a touch sharp, a fifth, plus a quiet octave-up sawtooth
    // edge. Wider detune + the sawtooth partial give a lo-fi, slightly
    // out-of-tune brushed-metal/CRT shimmer (vs. the old clean sine+triangle
    // warmth) while staying under the visuals. The sawtooth voice is kept low so
    // it colours the tone metallic rather than dominating.
    const voices = [
      { mult: 1.0, detune: -8, type: "sine", gain: 1.0 },
      { mult: 1.0, detune: +9, type: "triangle", gain: 0.55 },
      { mult: 1.5, detune: -4, type: "triangle", gain: 0.4 },
      { mult: 2.0, detune: +5, type: "sawtooth", gain: 0.16 },
    ];

    for (const v of voices) {
      const osc = ctx.createOscillator();
      osc.type = v.type;
      osc.frequency.value = root * v.mult;
      osc.detune.value = v.detune;
      const vg = ctx.createGain();
      vg.gain.value = v.gain * 0.5;
      osc.connect(vg);
      vg.connect(this.droneFilter);
      osc.start();
      this.droneOscs.push(osc);
    }
  }

  // Derive a distinct "tongue" for a station index: each speaks at a different
  // pitch, tempo, and with a different vowel bias / formant scaling, so the
  // stations sound like different unknown languages. Deterministic per index
  // (no Math.random here — character is stable; the cadence varies at runtime).
  _voiceParamsFor(idx) {
    const i = ((idx % STATION_ROOTS.length) + STATION_ROOTS.length) %
      STATION_ROOTS.length;
    // Fundamentals spread across a believable vocal range (~100-150Hz).
    const funds = [108, 132, 118, 146, 124];
    // Formant scaling: <1 = larger/darker speaker, >1 = smaller/brighter.
    const scales = [0.92, 1.08, 1.0, 1.14, 0.97];
    // Base syllable tempo (ms between syllable onsets, before jitter).
    const tempos = [200, 150, 240, 170, 210];
    // Bias the vowel picker toward a couple of vowels so each tongue has a
    // recognisable "accent".
    const biases = [
      ["a", "o", "u"],
      ["e", "i", "a"],
      ["o", "u", "o"],
      ["i", "e", "i"],
      ["u", "a", "o"],
    ];
    return {
      fundamental: funds[i],
      formantScale: scales[i],
      tempo: tempos[i],
      bias: biases[i],
    };
  }

  // (Re)build the per-station voice source + formant filters + consonant burst.
  // Tears down the previous set first. Pass null to leave the voice sourceless
  // (the scheduler is stopped separately via _applyVoice).
  _buildVoiceFor(idx) {
    if (!this.ctx || !this.voiceSyllGain) return;
    const ctx = this.ctx;

    // Stop & disconnect any previous per-station voice nodes.
    this._teardownVoiceSource();

    this.currentVoiceIdx = idx;
    if (idx == null) {
      this.voiceParams = null;
      return;
    }

    const p = this._voiceParamsFor(idx);
    this.voiceParams = p;

    // --- Glottal source: a sawtooth at the vocal fundamental (rich harmonics
    // for the formants to carve), with gentle vibrato via a tiny LFO on detune.
    const osc = ctx.createOscillator();
    osc.type = "sawtooth";
    osc.frequency.value = p.fundamental;

    const vib = ctx.createOscillator();
    vib.type = "sine";
    vib.frequency.value = 4.7; // ~5Hz human-ish vibrato
    const vibGain = ctx.createGain();
    vibGain.gain.value = 6; // +/- ~6 cents of wobble
    vib.connect(vibGain);
    vibGain.connect(osc.detune);

    // --- Three parallel bandpass formants (F1,F2,F3). Tame the source level a
    // little going in so summing three resonant peaks doesn't clip.
    const preGain = ctx.createGain();
    preGain.gain.value = 0.5;
    osc.connect(preGain);

    const startVowel = VOWEL_FORMANTS[p.bias[0]] || VOWEL_FORMANTS.a;
    const formants = [];
    const fQ = [9, 11, 12]; // higher Q on upper formants = clearer vowels
    const fGain = [1.0, 0.7, 0.45];
    for (let k = 0; k < 3; k++) {
      const bp = ctx.createBiquadFilter();
      bp.type = "bandpass";
      bp.frequency.value = startVowel[k] * p.formantScale;
      bp.Q.value = fQ[k];
      const fg = ctx.createGain();
      fg.gain.value = fGain[k];
      preGain.connect(bp);
      bp.connect(fg);
      fg.connect(this.voiceSyllGain);
      formants.push(bp);
    }

    osc.start();
    vib.start();

    this.voiceOsc = osc;
    this.voiceVibLfo = vib;
    this.voiceVibGain = vibGain;
    this.voiceFormants = formants;
    this.voicePreGain = preGain;

    // --- Consonant burst: a short filtered-noise ping fired at some syllable
    // onsets so the stream reads as words, not just vowels. Looping noise ->
    // bandpass -> envelope gain (normally shut) -> transmission filter.
    const cnoise = ctx.createBufferSource();
    cnoise.buffer = this._makeNoiseBuffer(ctx, 1.0);
    cnoise.loop = true;
    const cfilt = ctx.createBiquadFilter();
    cfilt.type = "highpass";
    cfilt.frequency.value = 1800;
    cfilt.Q.value = 0.7;
    const cgain = ctx.createGain();
    cgain.gain.value = 0;
    cnoise.connect(cfilt);
    cfilt.connect(cgain);
    cgain.connect(this.voiceTransFilter);
    cnoise.start();

    this.voiceConsNoise = cnoise;
    this.voiceConsFilter = cfilt;
    this.voiceConsGain = cgain;
  }

  // Stop & disconnect the per-station voice source nodes (leaves the fixed
  // downstream chain — syllable gain / transmission filter / voiceGain — alive).
  _teardownVoiceSource() {
    const stop = (n) => {
      if (!n) return;
      try {
        n.stop();
      } catch (e) {}
      try {
        n.disconnect();
      } catch (e) {}
    };
    const cut = (n) => {
      if (!n) return;
      try {
        n.disconnect();
      } catch (e) {}
    };
    stop(this.voiceOsc);
    stop(this.voiceVibLfo);
    stop(this.voiceConsNoise);
    cut(this.voiceVibGain);
    cut(this.voicePreGain);
    for (const f of this.voiceFormants) cut(f);
    cut(this.voiceConsFilter);
    cut(this.voiceConsGain);
    this.voiceOsc = null;
    this.voiceVibLfo = null;
    this.voiceVibGain = null;
    this.voicePreGain = null;
    this.voiceFormants = [];
    this.voiceConsFilter = null;
    this.voiceConsGain = null;
    this.voiceConsNoise = null;
  }

  // (Re)select the melodic motif for a given station index. Cheap: the per-note
  // oscillators are ephemeral (created in _emitNote), so this only swaps the
  // motif table entry + applies the station's pluck cutoff and resets the step
  // cursor. Pass null to leave the melody motif-less (the sequencer is stopped
  // separately via _applyMelody).
  _buildMelodyFor(idx) {
    if (!this.ctx || !this.melodyFilter) return;
    this.currentMelodyIdx = idx;
    this.melodyStep = 0;
    if (idx == null) {
      this.melodyParams = null;
      return;
    }
    const p = MELODIES[((idx % MELODIES.length) + MELODIES.length) %
      MELODIES.length];
    this.melodyParams = p;
    // Apply this station's pluck character to the shared lowpass.
    this.melodyFilter.frequency.setTargetAtTime(
      p.cutoff,
      this.ctx.currentTime,
      0.05
    );
  }

  // Begin (or keep) the self-rescheduling note chain. Idempotent: calling it
  // while already running is a no-op (no stacked timers).
  _startMelodySequencer() {
    if (this.melodyRunning) return;
    if (!this.ctx || !this.melodyBus || !this.melodyParams) return;
    this.melodyRunning = true;
    this._emitNote();
  }

  // Stop the sequencer. The ephemeral note oscs self-stop after their decay, so
  // there is nothing to gate shut here — just kill the timer.
  _stopMelodySequencer() {
    this.melodyRunning = false;
    if (this.melodyTimer != null) {
      clearTimeout(this.melodyTimer);
      this.melodyTimer = null;
    }
  }

  // Play one motif note as a fresh short-lived osc -> gain (ADSR pluck) into the
  // melody bus, then schedule the next via setTimeout (NOT rAF, NOT a per-frame
  // loop). Loops the motif with slight algorithmic variation — occasional octave
  // jump, neighbour-note swap, or rest — so it evolves rather than repeating.
  _emitNote() {
    this.melodyTimer = null;
    // Bail defensively if state changed out from under a pending timer.
    if (!this.melodyRunning || !this.ctx || !this.melodyFilter) return;
    if (!this._audible() || this.tunedIdx == null) {
      this._stopMelodySequencer();
      return;
    }
    const p = this.melodyParams;
    if (!p || !p.notes || !p.notes.length) {
      this._stopMelodySequencer();
      return;
    }
    const ctx = this.ctx;
    const now = ctx.currentTime;

    // Advance the motif cursor (loops at the end).
    const i = this.melodyStep % p.notes.length;
    this.melodyStep = (this.melodyStep + 1) % p.notes.length;

    let midi = p.notes[i];

    // --- Algorithmic variation (keeps everything on the Ambulate scale):
    //   ~10% rest, ~12% octave jump, ~12% neighbour-note swap.
    let rest = midi == null;
    if (!rest) {
      const r = Math.random();
      if (r < 0.1) {
        rest = true; // turn this note into a rest occasionally
      } else if (r < 0.22) {
        midi += Math.random() < 0.5 ? 12 : -12; // octave jump
      } else if (r < 0.34) {
        // Swap to a neighbouring on-scale degree in the same octave.
        const oct = Math.floor((midi - MIDI_A2) / 12);
        const pc = SCALE_PCS[(Math.random() * SCALE_PCS.length) | 0];
        midi = MIDI_A2 + oct * 12 + pc;
      }
    }

    if (!rest) {
      const freq = midiToFreq(midi);
      const osc = ctx.createOscillator();
      osc.type = p.type;
      osc.frequency.value = freq;

      // Percussive ADSR pluck: fast attack, short exponential-ish decay to 0.
      const g = ctx.createGain();
      g.gain.value = 0;
      const attack = 0.008;
      const decay = p.decay * (0.85 + Math.random() * 0.3);
      const peak = 0.8 + Math.random() * 0.2;
      g.gain.setValueAtTime(0, now);
      g.gain.linearRampToValueAtTime(peak, now + attack);
      // setTargetAtTime toward 0 = smooth percussive tail (no click).
      g.gain.setTargetAtTime(0, now + attack, decay * 0.4);

      osc.connect(g);
      g.connect(this.melodyFilter);
      osc.start(now);
      // Stop comfortably after the tail has decayed; node is then GC'd.
      const life = attack + decay + 0.2;
      osc.stop(now + life);
      // Disconnect once stopped so nothing lingers in the graph.
      osc.onended = () => {
        try {
          osc.disconnect();
        } catch (e) {}
        try {
          g.disconnect();
        } catch (e) {}
      };
    }

    // --- Schedule the next step: the motif's base step, lightly humanised, with
    // an occasional longer pause so phrases breathe.
    let gap = p.step * (0.9 + Math.random() * 0.2);
    if (Math.random() < 0.12) gap += p.step * (0.5 + Math.random()); // breath
    this.melodyTimer = setTimeout(() => this._emitNote(), gap);
  }

  // --- Power-on WARM-UP cue -------------------------------------------------
  // A short CRT/tube radio bring-up sound to accompany the visual boot sequence:
  //   1. a dry relay/filament TICK (a tiny click as the set latches on),
  //   2. a tube/oscillator HUM that swells up from a low rumble and settles,
  //   3. a STATIC swell that rushes in then pulls back as the front-end "warms".
  // Everything is ephemeral (self-stopping oscillators + a short noise burst on
  // their own gains, NOT the live drone/static buses) so it never disturbs the
  // steady-state graph or its gating. Safe to call only after ensure() has built
  // the graph + resumed the context; no-ops otherwise. Honors the global mute.
  boot() {
    if (this.disposed) return;
    if (!this.ctx || !this.master) return;
    if (this.sound === false) return; // respect the global Music mute
    const ctx = this.ctx;
    const now = ctx.currentTime;

    // Small dedicated sub-bus so the whole cue rides under the master and never
    // touches the live drone/static/voice/melody gains.
    const bus = ctx.createGain();
    bus.gain.value = 0.9;
    bus.connect(this.master);

    // --- 1) Relay/filament TICK: a very short filtered noise click. -----------
    const tick = ctx.createBufferSource();
    tick.buffer = this._makeNoiseBuffer(ctx, 0.05);
    const tickFilt = ctx.createBiquadFilter();
    tickFilt.type = "bandpass";
    tickFilt.frequency.value = 2200;
    tickFilt.Q.value = 0.8;
    const tickGain = ctx.createGain();
    tickGain.gain.setValueAtTime(0.0001, now);
    tickGain.gain.linearRampToValueAtTime(0.5, now + 0.004);
    tickGain.gain.setTargetAtTime(0, now + 0.01, 0.02);
    tick.connect(tickFilt);
    tickFilt.connect(tickGain);
    tickGain.connect(bus);
    tick.start(now);
    tick.stop(now + 0.12);

    // --- 2) Tube/filament HUM: a low sine that sweeps up and settles. ---------
    const hum = ctx.createOscillator();
    hum.type = "sine";
    hum.frequency.setValueAtTime(48, now + 0.02);
    hum.frequency.exponentialRampToValueAtTime(120, now + 0.55);
    const hum2 = ctx.createOscillator(); // a quiet harmonic for body
    hum2.type = "triangle";
    hum2.frequency.setValueAtTime(96, now + 0.02);
    hum2.frequency.exponentialRampToValueAtTime(240, now + 0.55);
    const humGain = ctx.createGain();
    humGain.gain.setValueAtTime(0.0001, now);
    humGain.gain.exponentialRampToValueAtTime(0.13, now + 0.4); // warm up
    humGain.gain.setTargetAtTime(0, now + 0.7, 0.22); // settle out
    const hum2Gain = ctx.createGain();
    hum2Gain.gain.value = 0.04;
    hum.connect(humGain);
    hum2.connect(hum2Gain);
    hum2Gain.connect(humGain);
    humGain.connect(bus);
    hum.start(now);
    hum2.start(now);
    hum.stop(now + 1.3);
    hum2.stop(now + 1.3);

    // --- 3) STATIC swell: a noise rush that crests then recedes as the dial
    // "calibrates", handing off to the steady-state static/station once live.
    const swell = ctx.createBufferSource();
    swell.buffer = this._makeNoiseBuffer(ctx, 1.4);
    const swFilt = ctx.createBiquadFilter();
    swFilt.type = "bandpass";
    swFilt.frequency.setValueAtTime(900, now);
    swFilt.frequency.exponentialRampToValueAtTime(4200, now + 0.6);
    swFilt.Q.value = 0.5;
    const swGain = ctx.createGain();
    swGain.gain.setValueAtTime(0.0001, now + 0.08);
    swGain.gain.linearRampToValueAtTime(0.12, now + 0.45); // rush in
    swGain.gain.setTargetAtTime(0, now + 0.6, 0.3); // pull back
    swell.connect(swFilt);
    swFilt.connect(swGain);
    swGain.connect(bus);
    swell.start(now + 0.05);
    swell.stop(now + 1.5);

    // Disconnect the whole cue bus once everything has decayed so nothing lingers
    // in the graph (the nodes self-stop; this just frees the sub-bus).
    const cleanup = () => {
      try {
        bus.disconnect();
      } catch (e) {}
    };
    swell.onended = cleanup;
  }

  // --- Power-off SHUTDOWN cue -----------------------------------------------
  // The mirror of boot(): a short CRT/tube radio power-DOWN sound to accompany
  // the visual shutdown sequence:
  //   1. a dying-CRT WHINE that pitches DOWN and fades as the high-voltage line
  //      collapses (the inverse of the warm-up hum that swelled up),
  //   2. a STATIC die-out — a brief noise gasp that closes off as the front-end
  //      loses power (the inverse of the warm-up static swell),
  //   3. a dry relay CLUNK as the set latches off (the inverse of the warm-up
  //      relay tick).
  // Everything is ephemeral (self-stopping oscillators + short noise bursts on
  // their own gains, NOT the live drone/static/voice/melody buses) so it never
  // disturbs the steady-state graph or its gating; the live buses are ducked to
  // silence separately by setPower(false) -> _apply* right after this fires.
  // Safe to call only after ensure() has built the graph; no-ops otherwise.
  // Honors the global mute.
  shutdown() {
    if (this.disposed) return;
    if (!this.ctx || !this.master) return;
    if (this.sound === false) return; // respect the global Music mute
    const ctx = this.ctx;
    const now = ctx.currentTime;

    // Small dedicated sub-bus so the whole cue rides under the master and never
    // touches the live drone/static/voice/melody gains.
    const bus = ctx.createGain();
    bus.gain.value = 0.9;
    bus.connect(this.master);

    // --- 1) Dying-CRT WHINE: a tone that pitches DOWN from a thin high whine to
    // a low collapse, fading out — the flyback/HV line losing power. -----------
    const whine = ctx.createOscillator();
    whine.type = "sine";
    whine.frequency.setValueAtTime(220, now);
    whine.frequency.exponentialRampToValueAtTime(36, now + 0.6); // pitch down
    const whine2 = ctx.createOscillator(); // a thin harmonic for the CRT edge
    whine2.type = "triangle";
    whine2.frequency.setValueAtTime(440, now);
    whine2.frequency.exponentialRampToValueAtTime(72, now + 0.6);
    const whineGain = ctx.createGain();
    whineGain.gain.setValueAtTime(0.12, now);
    whineGain.gain.setTargetAtTime(0, now + 0.18, 0.16); // bleed away
    const whine2Gain = ctx.createGain();
    whine2Gain.gain.value = 0.035;
    whine.connect(whineGain);
    whine2.connect(whine2Gain);
    whine2Gain.connect(whineGain);
    whineGain.connect(bus);
    whine.start(now);
    whine2.start(now);
    whine.stop(now + 0.85);
    whine2.stop(now + 0.85);

    // --- 2) STATIC die-out: a short noise gasp that closes off as the front-end
    // loses power (mirror of boot's swell, but collapsing instead of rushing). -
    const gasp = ctx.createBufferSource();
    gasp.buffer = this._makeNoiseBuffer(ctx, 0.8);
    const gaspFilt = ctx.createBiquadFilter();
    gaspFilt.type = "bandpass";
    gaspFilt.frequency.setValueAtTime(4200, now);
    gaspFilt.frequency.exponentialRampToValueAtTime(500, now + 0.45); // close down
    gaspFilt.Q.value = 0.5;
    const gaspGain = ctx.createGain();
    gaspGain.gain.setValueAtTime(0.1, now);
    gaspGain.gain.setTargetAtTime(0, now + 0.05, 0.14); // die out fast
    gasp.connect(gaspFilt);
    gaspFilt.connect(gaspGain);
    gaspGain.connect(bus);
    gasp.start(now);
    gasp.stop(now + 0.8);

    // --- 3) Relay/contactor CLUNK: a short low filtered-noise thud as the set
    // latches off, landing just after the picture collapses. -------------------
    const clunkAt = now + 0.5;
    const clunk = ctx.createBufferSource();
    clunk.buffer = this._makeNoiseBuffer(ctx, 0.06);
    const clunkFilt = ctx.createBiquadFilter();
    clunkFilt.type = "lowpass";
    clunkFilt.frequency.value = 320; // low, woody thunk
    clunkFilt.Q.value = 0.9;
    const clunkGain = ctx.createGain();
    clunkGain.gain.setValueAtTime(0.0001, clunkAt);
    clunkGain.gain.linearRampToValueAtTime(0.45, clunkAt + 0.006);
    clunkGain.gain.setTargetAtTime(0, clunkAt + 0.012, 0.03);
    clunk.connect(clunkFilt);
    clunkFilt.connect(clunkGain);
    clunkGain.connect(bus);
    clunk.start(clunkAt);
    clunk.stop(clunkAt + 0.2);

    // Disconnect the whole cue bus once everything has decayed so nothing lingers
    // in the graph (the nodes self-stop; this just frees the sub-bus).
    const cleanup = () => {
      try {
        bus.disconnect();
      } catch (e) {}
    };
    clunk.onended = cleanup;
  }

  // --- Logical state setters -----------------------------------------------
  setPower(on) {
    this.power = !!on;
    this._applyMaster();
    this._applyDrone();
    this._applyStatic();
    this._applyVoice();
    this._applyMelody();
  }

  setSound(on) {
    this.sound = on !== false;
    this._applyMaster();
    this._applyVoice();
    this._applyMelody();
  }

  // Update tuning. `idx` is the locked station index (null = between stations);
  // `staticAmount` is 0 (dead locked) .. 1 (max static), driving hiss level.
  setTuning(idx, staticAmount) {
    if (typeof staticAmount === "number") {
      this.staticAmount = Math.max(0, Math.min(1, staticAmount));
    }
    if (idx !== this.tunedIdx) {
      this.tunedIdx = idx;
      // Rebuild oscillators + voice for the new station (or silence them).
      if (this.ctx) {
        this._buildOscsFor(idx);
        this._buildVoiceFor(idx);
        this._buildMelodyFor(idx);
      }
    }
    this._applyStatic();
    this._applyDrone();
    this._applyVoice();
    this._applyMelody();
  }

  // Whether the rig should actually make sound right now.
  _audible() {
    return this.power && this.sound !== false;
  }

  // Read the REAL output spectrum into `outArray` as `n` normalized 0..1 bands
  // (low frequency -> high). The meter calls this every rAF tick instead of
  // faking motion: the analyser sees the full master mix, so the levels reflect
  // exactly what is playing — static hiss between stations, music when tuned.
  //
  // No-ops safely (writes zeros) when there's no context/analyser yet, so the
  // caller can poll unconditionally. The analyser's byte buffer is reused.
  getLevels(outArray, n) {
    if (!outArray || !n) return;
    const a = this.analyser;
    const buf = this.analyserData;
    if (!a || !buf) {
      for (let i = 0; i < n; i++) outArray[i] = 0;
      return;
    }
    a.getByteFrequencyData(buf); // 0..255 per bin, into the reused buffer
    const bins = buf.length;
    // Split the bins into n contiguous bands (low->high) and average each.
    const per = bins / n;
    for (let i = 0; i < n; i++) {
      const start = (i * per) | 0;
      let end = ((i + 1) * per) | 0;
      if (end <= start) end = start + 1;
      if (end > bins) end = bins;
      let sum = 0;
      for (let j = start; j < end; j++) sum += buf[j];
      const avg = sum / (end - start);
      outArray[i] = avg / 255; // normalize to 0..1
    }
  }

  // Single-number sibling of getLevels(): the OVERALL current loudness as one
  // 0..1 value, for the shared audioBus level provider (the faceless dancers
  // read it via audioBus.getLevel()). Averages every frequency bin of the same
  // master-tap analyser the SIGNAL meter uses, so it tracks exactly what's
  // playing — static hiss between stations, the station's music when tuned.
  // No-ops to 0 when there's no context/analyser yet, so callers can poll
  // unconditionally; the analyser's byte buffer is reused (no per-frame GC).
  getLevel() {
    const a = this.analyser;
    const buf = this.analyserData;
    if (!a || !buf) return 0;
    a.getByteFrequencyData(buf); // 0..255 per bin, into the reused buffer
    const bins = buf.length;
    if (!bins) return 0;
    let sum = 0;
    for (let i = 0; i < bins; i++) sum += buf[i];
    return sum / bins / 255; // mean bin / 255 -> 0..1
  }

  _applyMaster() {
    if (!this.ctx || !this.master) return;
    const target = this._audible() ? 1 : 0;
    this.master.gain.setTargetAtTime(target, this.ctx.currentTime, MASTER_TAU);
  }

  _applyStatic() {
    if (!this.ctx || !this.noiseGain) return;
    // Static is only meaningful when powered; when silent we still ramp the
    // sub-gain so coming back on doesn't pop.
    const audible = this._audible();
    const level = audible ? STATIC_MAX * this.staticAmount : 0;
    this.noiseGain.gain.setTargetAtTime(
      level,
      this.ctx.currentTime,
      STATIC_TAU
    );
  }

  _applyDrone() {
    if (!this.ctx || !this.droneGain || !this.lfoGain) return;
    const audible = this._audible();
    const tuned = this.tunedIdx != null;
    // Crossfade the drone in only while tuned & audible. The further the needle
    // sits from the station the quieter the drone (mirror of the static curve).
    const lockFactor = 1 - this.staticAmount; // 1 at lock, 0 far away
    const level = audible && tuned ? DRONE_LEVEL * lockFactor : 0;
    this.droneGain.gain.setTargetAtTime(
      level,
      this.ctx.currentTime,
      DRONE_TAU
    );
    // Tremolo depth scales with the drone level so a silent drone doesn't wobble.
    const depth = audible && tuned ? 0.03 * lockFactor : 0;
    this.lfoGain.gain.setTargetAtTime(depth, this.ctx.currentTime, DRONE_TAU);
  }

  // Crossfade the glossolalia voice and run/stop its syllable scheduler. The
  // voice is only audible (and the scheduler only runs) when audible & tuned;
  // its level mirrors the drone's lockFactor so it's loud dead-on a station and
  // ducks under the static as the needle drifts away.
  _applyVoice() {
    if (!this.ctx || !this.voiceGain) return;
    const audible = this._audible();
    const tuned = this.tunedIdx != null;
    const on = audible && tuned;
    const lockFactor = 1 - this.staticAmount; // 1 at lock, 0 far away
    const level = on ? VOICE_LEVEL * lockFactor : 0;
    this.voiceGain.gain.setTargetAtTime(level, this.ctx.currentTime, VOICE_TAU);

    if (on) this._startVoiceScheduler();
    else this._stopVoiceScheduler();
  }

  // Crossfade the station's melodic motif and run/stop its note sequencer. Same
  // gating as the drone/voice: only audible (and only scheduling) when audible &
  // tuned; its bus level mirrors the drone's lockFactor so the tune is loud
  // dead-on a station and ducks under the static as the needle drifts away. The
  // peak sits a touch under the drone so it weaves through rather than dominates.
  _applyMelody() {
    if (!this.ctx || !this.melodyBus) return;
    const audible = this._audible();
    const tuned = this.tunedIdx != null;
    const on = audible && tuned;
    const lockFactor = 1 - this.staticAmount; // 1 at lock, 0 far away
    const level = on ? MELODY_LEVEL * lockFactor : 0;
    this.melodyBus.gain.setTargetAtTime(
      level,
      this.ctx.currentTime,
      MELODY_TAU
    );

    if (on) this._startMelodySequencer();
    else this._stopMelodySequencer();
  }

  // Begin (or keep) the self-rescheduling syllable chain. Idempotent: calling it
  // while already running is a no-op (no stacked timers).
  _startVoiceScheduler() {
    if (this.voiceRunning) return;
    if (!this.ctx || !this.voiceSyllGain || !this.voiceParams) return;
    this.voiceRunning = true;
    this._emitSyllable();
  }

  // Stop the scheduler and shut the syllable gate so no half-open vowel hangs.
  _stopVoiceScheduler() {
    this.voiceRunning = false;
    if (this.voiceTimer != null) {
      clearTimeout(this.voiceTimer);
      this.voiceTimer = null;
    }
    if (this.ctx && this.voiceSyllGain) {
      this.voiceSyllGain.gain.setTargetAtTime(
        0,
        this.ctx.currentTime,
        SYLLABLE_TAU
      );
    }
  }

  // Emit one syllable, then schedule the next via setTimeout (NOT rAF, NOT a
  // per-frame loop). Each call: morph the formants toward a chosen vowel, pulse
  // the syllable gate open then closed, sometimes fire a consonant burst, and
  // group syllables into word-like runs with the occasional breath pause.
  _emitSyllable() {
    this.voiceTimer = null;
    // Bail defensively if state changed out from under a pending timer.
    if (!this.voiceRunning || !this.ctx || !this.voiceSyllGain) return;
    if (!this._audible() || this.tunedIdx == null) {
      this._stopVoiceScheduler();
      return;
    }
    const p = this.voiceParams;
    if (!p) {
      this._stopVoiceScheduler();
      return;
    }
    const ctx = this.ctx;
    const now = ctx.currentTime;

    // --- Pick the next vowel (biased toward this station's "accent", with the
    // occasional repeat or wildcard for naturalness).
    let vowel;
    const r = Math.random();
    if (r < 0.55) vowel = p.bias[(Math.random() * p.bias.length) | 0];
    else if (r < 0.7) vowel = this.voiceVowel; // hold the same vowel
    else vowel = VOWEL_KEYS[(Math.random() * VOWEL_KEYS.length) | 0];
    this.voiceVowel = vowel;
    const targets = VOWEL_FORMANTS[vowel] || VOWEL_FORMANTS.a;

    // --- Glide the three formant filters toward the vowel (40-90ms morph).
    const glide = 0.04 + Math.random() * 0.05;
    for (let k = 0; k < this.voiceFormants.length; k++) {
      const f = this.voiceFormants[k];
      if (!f) continue;
      f.frequency.setTargetAtTime(
        targets[k] * p.formantScale,
        now,
        glide
      );
    }

    // --- Slight pitch drift on the glottal source so the voice isn't monotone.
    if (this.voiceOsc) {
      const drift = p.fundamental * (1 + (Math.random() - 0.5) * 0.06);
      this.voiceOsc.frequency.setTargetAtTime(drift, now, 0.08);
    }

    // --- Open the syllable gate to a varied loudness, then close it.
    const dur = 0.11 + Math.random() * 0.15; // ~110-260ms voiced portion
    const peak = 0.7 + Math.random() * 0.3;
    this.voiceSyllGain.gain.setTargetAtTime(peak, now, SYLLABLE_TAU);
    this.voiceSyllGain.gain.setTargetAtTime(
      0,
      now + dur,
      SYLLABLE_TAU
    );

    // --- Occasionally fire a consonant burst at the onset so it reads as words.
    if (this.voiceConsGain && Math.random() < 0.5) {
      const plosive = Math.random() < 0.5;
      if (this.voiceConsFilter) {
        // Plosive = lower, punchier; fricative = higher, hissier.
        this.voiceConsFilter.type = plosive ? "bandpass" : "highpass";
        this.voiceConsFilter.frequency.setTargetAtTime(
          plosive ? 900 + Math.random() * 600 : 2600 + Math.random() * 1800,
          now,
          0.005
        );
      }
      const cdur = 0.02 + Math.random() * 0.02; // 20-40ms ping
      const cpk = plosive ? 0.5 : 0.32;
      const cg = this.voiceConsGain.gain;
      cg.setTargetAtTime(cpk, now, 0.004);
      cg.setTargetAtTime(0, now + cdur, 0.01);
    }

    // --- Schedule the next syllable: short inter-syllable gap most of the time,
    // an occasional longer word/breath pause so it groups into utterances.
    const base = p.tempo;
    let gap;
    if (Math.random() < 0.22) {
      gap = 400 + Math.random() * 500; // 400-900ms breath / word boundary
    } else {
      gap = dur * 1000 + 40 + Math.random() * 80; // 40-120ms inter-syllable
      gap = Math.max(gap, base * (0.7 + Math.random() * 0.6));
    }
    this.voiceTimer = setTimeout(() => this._emitSyllable(), gap);
  }

  // --- Teardown ------------------------------------------------------------
  dispose() {
    this.disposed = true;
    // Always kill the schedulers even if the graph was never built.
    this._stopVoiceScheduler();
    this._stopMelodySequencer();
    if (!this.ctx) return;
    const stop = (n) => {
      try {
        n.stop();
      } catch (e) {}
    };
    const cut = (n) => {
      try {
        n.disconnect();
      } catch (e) {}
    };

    for (const o of this.droneOscs) {
      stop(o);
      cut(o);
    }
    this.droneOscs = [];
    if (this.lfo) {
      stop(this.lfo);
      cut(this.lfo);
    }
    if (this.noiseSrc) {
      stop(this.noiseSrc);
      cut(this.noiseSrc);
    }
    // Voice: stop the per-station source nodes, then disconnect the fixed tail.
    this._teardownVoiceSource();
    // Disconnect the analyser tap (it's a sink off the master).
    if (this.analyser) cut(this.analyser);
    [
      this.lfoGain,
      this.droneFilter,
      this.droneGain,
      this.noiseGain,
      this.noiseFilter,
      this.voiceSyllGain,
      this.voiceTransFilter,
      this.voiceGain,
      this.melodyFilter,
      this.melodyBus,
      this.master,
    ].forEach((n) => n && cut(n));

    if (this.ctx.close) this.ctx.close().catch(() => {});
    this.ctx = null;
    this.master = null;
    this.analyser = null;
    this.analyserData = null;
    this.noiseSrc = null;
    this.noiseFilter = null;
    this.noiseGain = null;
    this.droneGain = null;
    this.droneFilter = null;
    this.lfo = null;
    this.lfoGain = null;
    this.voiceGain = null;
    this.voiceTransFilter = null;
    this.voiceSyllGain = null;
    this.voiceFormants = [];
    this.voiceParams = null;
    this.currentVoiceIdx = null;
    this.melodyBus = null;
    this.melodyFilter = null;
    this.melodyParams = null;
    this.currentMelodyIdx = null;
  }
}
